import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@shared/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        success: "border-status-success-border bg-status-success-bg text-status-success-text hover:bg-status-success-bg/80",
        warning: "border-status-warning-border bg-status-warning-bg text-status-warning-text hover:bg-status-warning-bg/80",
        danger: "border-status-error-border bg-status-error-bg text-status-error-text hover:bg-status-error-bg/80",
        secondary: "border-status-info-border bg-status-info-bg text-status-info-text hover:bg-status-info-bg/80",
        outline: "border-border bg-transparent text-muted-foreground hover:bg-muted/60",
        destructive: "border-status-error-border bg-status-error-bg text-status-error-text hover:bg-status-error-bg/80",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /** Show a status dot before the label */
  dot?: boolean
  /** Custom dot color (Tailwind bg class, e.g. "bg-green-500"). Defaults to "bg-current". */
  dotColor?: string
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, dot, dotColor = "bg-current", children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(badgeVariants({ variant }), className)}
        {...props}
      >
        {dot && (
          <span className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full mr-1.5", dotColor)} />
        )}
        {children}
      </div>
    )
  }
)

Badge.displayName = "Badge"

export { Badge }
// eslint-disable-next-line react-refresh/only-export-components
export { badgeVariants }
